import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ItemPresetInput = ({ value, disabled, presets, onChange }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const updateMenuPosition = React.useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    // Flip up if the dropdown would run off the bottom of the viewport
    const MAX_H = 240; // tailwind max-h-60 (15rem)
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openUp = spaceBelow < (MAX_H + gap) && spaceAbove > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? (window.innerHeight - rect.top + gap) : undefined,
      left: rect.left,
      width: rect.width,
      maxHeight: MAX_H,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const t = e.target;
      if (containerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    // capture=true so we catch scroll from overflow containers (like the table wrapper)
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  const choose = (next) => {
    onChange(next);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div ref={containerRef}>
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onClick={() => {
            if (disabled) return;
            updateMenuPosition();
            setOpen(true);
          }}
          onFocus={() => {
            if (disabled) return;
            updateMenuPosition();
            setOpen(true);
          }}
          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm disabled:bg-gray-100"
          placeholder="Select or type…"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            updateMenuPosition();
            setOpen((p) => !p);
          }}
          className="shrink-0 w-8 h-8 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
          aria-label="Show presets"
        >
          ▾
        </button>
      </div>

      {open && !disabled && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              style={menuStyle}
              className="bg-white border border-gray-200 rounded-md shadow-lg overflow-auto"
            >
              <button
                type="button"
                onClick={() => choose('')}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100"
              >
                Manual Input / Other
              </button>
              {(presets || []).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => choose(opt)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                >
                  {opt}
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

// Public Estimate View (Customer-facing)

export default ItemPresetInput;
